import * as ExcelJS from 'exceljs';
import i18n from '../locales/i18n';
import { TradeWithValuation } from '../types/valuation';

/**
 * Convert 1-based column index to Excel letter(s): 1→A, 26→Z, 27→AA, etc.
 */
function colIndexToLetter(colIndex: number): string {
  let result = '';
  let n = colIndex;
  while (n > 0) {
    const rem = (n - 1) % 26;
    result = String.fromCharCode(65 + rem) + result;
    n = Math.floor((n - 1) / 26);
  }
  return result;
}

/**
 * Determine trade type from settlement info (mirrors backend logic).
 */
function determineTradeType(
  settlementType: string,
  settlementCurrency: string,
  ccy1: string,
  ccy2: string,
): 'PHYSICAL' | 'NDF_CCY2' | 'NDF_CCY1' {
  if (settlementType.includes('ntrega')) return 'PHYSICAL';
  if (settlementType.includes('ompensaci')) {
    if (settlementCurrency === ccy1) return 'NDF_CCY1';
    return 'NDF_CCY2';
  }
  return 'PHYSICAL';
}

/**
 * Export valuation data with Excel formulas that replicate the MtM calculation.
 * Column order matches the AG Grid exactly.
 *
 * Column layout (matches grid):
 *   A  Method                (value: calc/manual)
 *   B  Trade #               (value)
 *   C  Entity                (value)
 *   D  Direction             (value: Buy/Sell)
 *   E  Pair                  (value)
 *   F  Settlement Type       (value)
 *   G  Amount (CCY1)         (value)
 *   H  Contra Amount (CCY2)  (value)
 *   I  Price (contracted)    (value)
 *   J  Counterparty          (value)
 *   K  Trade Date            (value)
 *   L  Value Date            (value)
 *   M  Maturity Date         (value)
 *   N  Grouping 1            (value)
 *   O  Grouping 2            (value)
 *   P  Grouping 3            (value)
 *   Q  Hedge Object          (value)
 *   R  Valuation Date        (value)
 *   S  MtM                   ** FORMULA ** (overall MtM in valuation currency)
 *   T  Prev Val Date         (value)
 *   U  Prev MtM              (value)
 *   V  MtM Change            ** FORMULA ** (S - U)
 *   W  Days to Settlement    (value)
 *   X  Spot Rate             (value)
 *   Y  Implied Forward Rate  ** FORMULA **
 *   Z  r(CCY1)               (value — interpolated input)
 *   AA DF(CCY1)              ** FORMULA **
 *   AB Day Count (CCY1)      (value)
 *   AC MtM(CCY1)             ** FORMULA **
 *   AD r(CCY2)               (value — interpolated input)
 *   AE DF(CCY2)              ** FORMULA **
 *   AF Day Count (CCY2)      (value)
 *   AG MtM(CCY2)             ** FORMULA **
 */
export const exportValuationToExcel = async (
  trades: TradeWithValuation[],
  valuationDate: string,
  label?: string,
) => {
  const t = i18n.t.bind(i18n);
  const workbook = new ExcelJS.Workbook();
  const sheetName = label || `MtM ${valuationDate}`;
  const worksheet = workbook.addWorksheet(sheetName.substring(0, 31));

  // Determine max fixing dates across all Asiático trades (for per-date triplet columns)
  let maxFixingDates = 0;
  trades.forEach((trade) => {
    const perDateRates = trade.currentValuation?.perDateRates;
    if (perDateRates && perDateRates.length > maxFixingDates) {
      maxFixingDates = perDateRates.length;
    }
  });

  // --- Headers (same order as AG Grid) ---
  const headers = [
    /* A  */ t('valuation.positions.method'),
    /* B  */ t('valuation.positions.tradeNumber'),
    /* C  */ t('valuation.positions.ourPartyShort'),
    /* D  */ t('valuation.positions.direction'),
    /* E  */ t('valuation.positions.pair'),
    /* F  */ t('valuation.positions.settlementType'),
    /* G  */ t('valuation.positions.amount'),
    /* H  */ t('valuation.positions.contraAmount'),
    /* I  */ t('valuation.positions.price'),
    /* J  */ t('valuation.positions.counterparty', 'Counterparty'),
    /* K  */ t('valuation.positions.tradeDate', 'Trade Date'),
    /* L  */ t('valuation.positions.valueDate', 'Value Date'),
    /* M  */ t('valuation.positions.maturityDate'),
    /* N  */ t('grid.columns.grouping1'),
    /* O  */ t('grid.columns.grouping2'),
    /* P  */ t('grid.columns.grouping3'),
    /* Q  */ t('valuation.positions.hedgeObject'),
    /* R  */ t('valuation.positions.valuationDate'),
    /* S  */ t('valuation.positions.mtm'),
    /* T  */ t('valuation.positions.prevValDate'),
    /* U  */ t('valuation.positions.prevMtm'),
    /* V  */ t('valuation.positions.mtmDelta'),
    /* W  */ t('valuation.positions.days'),
    /* X  */ t('valuation.positions.spotRate'),
    /* Y  */ t('valuation.positions.forwardRate'),
    /* Z  */ t('valuation.positions.rCcy1'),
    /* AA */ t('valuation.positions.dfCcy1'),
    /* AB */ t('valuation.positions.dccCcy1'),
    /* AC */ t('valuation.positions.mtmCcy1'),
    /* AD */ t('valuation.positions.rCcy2'),
    /* AE */ t('valuation.positions.dfCcy2'),
    /* AF */ t('valuation.positions.dccCcy2'),
    /* AG */ t('valuation.positions.mtmCcy2'),
    // --- Forward Asiático columns (AH onwards) ---
    /* AH */ t('valuation.positions.product'),
    /* AI */ t('valuation.positions.blendedRateDetail.title'),
    /* AJ */ t('valuation.positions.blendedRateDetail.realised') + ' #',
    /* AK */ t('valuation.positions.blendedRateDetail.projected') + ' #',
    // --- KAN-118: Methodology columns appended after per-date triplets below ---
  ];

  // Per-date triplet headers: Fixing Date N, Rate N, Source N
  for (let n = 1; n <= maxFixingDates; n++) {
    headers.push(`${t('valuation.positions.blendedRateDetail.date')} ${n}`);
    headers.push(`${t('valuation.positions.blendedRateDetail.rate')} ${n}`);
    headers.push(`${t('valuation.positions.blendedRateDetail.source')} ${n}`);
  }

  // KAN-118 methodology + IFRS 9 columns (after all Asiático columns)
  // Column indices: 38 + maxFixingDates*3 onwards (1-indexed)
  const kan118BaseCol = 38 + maxFixingDates * 3; // 1-indexed
  headers.push(t('valuation.positions.forwardRateSource'));        // kan118BaseCol
  headers.push(t('valuation.positions.discountingMethod'));         // kan118BaseCol+1
  headers.push(t('valuation.positions.interpolatedFwdPoints'));     // kan118BaseCol+2
  headers.push(t('valuation.positions.undiscountedPnl'));           // kan118BaseCol+3
  headers.push(t('valuation.positions.spotAtTrade'));               // kan118BaseCol+4
  headers.push(t('valuation.positions.forwardPointsAtTrade'));      // kan118BaseCol+5
  headers.push(t('valuation.positions.spotElement'));               // kan118BaseCol+6  ** FORMULA **
  headers.push(t('valuation.positions.forwardElement'));            // kan118BaseCol+7
  headers.push('Ann (CCY1)');                                      // kan118BaseCol+8
  headers.push('Ann (CCY2)');                                      // kan118BaseCol+9

  // Title row
  worksheet.getCell('A1').value = `${t('valuation.positions.title')} — ${valuationDate}`;
  worksheet.mergeCells(1, 1, 1, headers.length);
  worksheet.getCell('A1').font = { bold: true, size: 14 };
  worksheet.getCell('A1').alignment = { horizontal: 'left' };

  // Methodology row (updated per KAN-118 — methodology is now per-trade)
  worksheet.getCell('A2').value = 'Forward MtM Valuation — see Forward Rate Source and Discounting Method columns';
  worksheet.getCell('A2').font = { italic: true, size: 10, color: { argb: 'FF888888' } };
  worksheet.mergeCells(2, 1, 2, 8);

  // Header row (row 3)
  const headerRow = worksheet.getRow(3);
  headerRow.values = headers;
  headerRow.font = { bold: true, size: 10 };
  headerRow.alignment = { horizontal: 'center', wrapText: true };
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF2D2D2D' },
    };
    cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
    cell.border = {
      bottom: { style: 'thin', color: { argb: 'FF555555' } },
    };
  });


  // Pre-compute KAN-118 column letters for use in formulas
  const fwdRateSourceCol = colIndexToLetter(kan118BaseCol);       // Forward Rate Source
  const discMethodCol = colIndexToLetter(kan118BaseCol + 1);      // Discounting Method
  const fwdPointsCol = colIndexToLetter(kan118BaseCol + 2);       // Interpolated Fwd Points
  const annCcy1Col = colIndexToLetter(kan118BaseCol + 8);         // Annualisation (CCY1)
  const annCcy2Col = colIndexToLetter(kan118BaseCol + 9);         // Annualisation (CCY2)

  // --- Data rows with formulas ---
  trades.forEach((trade, idx) => {
    const r = idx + 4; // Excel row number
    const cv = trade.currentValuation;
    const pv = trade.previousValuation;
    const row = worksheet.getRow(r);
    const isCalc = !!cv?.methodology;

    const tradeType = determineTradeType(
      trade.settlementType,
      trade.settlementCurrency || '',
      trade.currency1,
      trade.currency2,
    );

    // A: Method
    row.getCell(1).value = isCalc ? 'Calculated' : (cv ? 'Manual' : '');
    // B: Trade #
    row.getCell(2).value = trade.tradeNumber;
    // C: Entity
    row.getCell(3).value = trade.ourParty || '';
    // D: Direction
    row.getCell(4).value = trade.direction;
    // E: Pair
    row.getCell(5).value = `${trade.currency1}/${trade.currency2}`;
    // F: Settlement Type
    row.getCell(6).value = trade.settlementType;
    // G: Amount (CCY1)
    row.getCell(7).value = trade.quantityCurrency1;
    // H: Contra Amount (CCY2)
    row.getCell(8).value = trade.quantityCurrency2;
    // I: Price (contracted rate)
    row.getCell(9).value = trade.price;
    // J: Counterparty
    row.getCell(10).value = trade.counterpartyName || '';
    // K: Trade Date
    row.getCell(11).value = trade.tradeDate || '';
    // L: Value Date
    row.getCell(12).value = trade.valueDate || '';
    // M: Maturity Date
    row.getCell(13).value = trade.maturityDate || '';
    // N: Grouping 1
    row.getCell(14).value = trade.grouping1 || '';
    // O: Grouping 2
    row.getCell(15).value = trade.grouping2 || '';
    // P: Grouping 3
    row.getCell(16).value = trade.grouping3 || '';
    // Q: Hedge Object
    row.getCell(17).value = trade.hedgeObject || '';
    // R: Valuation Date
    row.getCell(18).value = cv?.valuationDate || '';
    // S: MtM (overall) — formula or value, handled below after AG
    // T: Prev Val Date
    row.getCell(20).value = pv?.valuationDate || '';
    // U: Prev MtM
    row.getCell(21).value = pv?.mtmCcy2 ?? pv?.mtmAmount ?? null;
    // V: MtM Change — formula: =S{r}-U{r}, handled below after S
    // W: Days to Settlement
    row.getCell(23).value = cv?.daysToSettlement ?? null;
    // X: Spot Rate
    row.getCell(24).value = cv?.spotRate ?? null;

    // --- Formula columns for calculated trades ---
    if (isCalc && cv?.rCcy1 != null && cv?.rCcy2 != null) {
      const dirFactor = `IF(D${r}="Buy",1,-1)`;
      const isAsiaticoCalc = trade.isAverageRateForward && cv.blendedRate != null;

      // Y: Forward Rate — methodology-aware
      //   CIRP:       Spot × DF(CCY1) / DF(CCY2) — automatically handles any annualisation method
      //   Fwd Points: Spot + interpolatedFwdPoints
      //   Asiático:   blended rate value (stored, not formulaic)
      if (isAsiaticoCalc) {
        row.getCell(25).value = cv.blendedRate;
      } else {
        const cirpFormula = `X${r}*AA${r}/AE${r}`;
        const fwdPtsFormula = `X${r}+${fwdPointsCol}${r}`;
        row.getCell(25).value = {
          formula: `IF(${fwdRateSourceCol}${r}="fwd_points",${fwdPtsFormula},${cirpFormula})`,
        } as any;
      }

      // Z: r(CCY1) — input value
      row.getCell(26).value = cv.rCcy1;
      // AA: DF(CCY1) — annualisation-aware
      //   simple:   1 / (1 + r × days / basis)
      //   compound: 1 / (1 + r)  [r is effective cumulative rate]
      //   daily:    (1 / (1 + r / basis)) ^ days
      row.getCell(27).value = {
        formula: `IF(${annCcy1Col}${r}="compound",1/(1+Z${r}),IF(${annCcy1Col}${r}="daily",(1/(1+Z${r}/IF(AB${r}="ACT/365",365,360)))^W${r},1/(1+Z${r}*W${r}/IF(AB${r}="ACT/365",365,360))))`,
      } as any;
      // AB: Day Count (CCY1)
      row.getCell(28).value = cv.dccCcy1 || '';

      // AD: r(CCY2) — input value
      row.getCell(30).value = cv.rCcy2;
      // AE: DF(CCY2) — annualisation-aware
      row.getCell(31).value = {
        formula: `IF(${annCcy2Col}${r}="compound",1/(1+AD${r}),IF(${annCcy2Col}${r}="daily",(1/(1+AD${r}/IF(AF${r}="ACT/365",365,360)))^W${r},1/(1+AD${r}*W${r}/IF(AF${r}="ACT/365",365,360))))`,
      } as any;
      // AF: Day Count (CCY2)
      row.getCell(32).value = cv.dccCcy2 || '';

      // AC & AG: MtM per currency — formulas depend on trade type and discounting method
      if (tradeType === 'PHYSICAL') {
        // two_leg:  MtM(CCY2) = dir * (Amount * DF_ccy1 * Spot - ContraAmt * DF_ccy2)
        // net_pnl:  MtM(CCY2) = dir * Amount * (FwdRate - ContractedRate) * DF_ccy2
        const twoLegFormula = `${dirFactor}*(G${r}*AA${r}*X${r}-H${r}*AE${r})`;
        const netPnlFormula = `${dirFactor}*G${r}*(Y${r}-I${r})*AE${r}`;
        row.getCell(33).value = {
          formula: `IF(${discMethodCol}${r}="net_pnl",${netPnlFormula},${twoLegFormula})`,
        } as any;
        // MtM(CCY1) = MtM(CCY2) / Spot
        row.getCell(29).value = {
          formula: `AG${r}/X${r}`,
        } as any;
      } else if (tradeType === 'NDF_CCY1') {
        // MtM(CCY1) = direction * Amount * (ImpliedFwd - ContractedRate) / ImpliedFwd * DF_ccy1
        row.getCell(29).value = {
          formula: `${dirFactor}*G${r}*(Y${r}-I${r})/Y${r}*AA${r}`,
        } as any;
        // MtM(CCY2) = MtM(CCY1) * Spot
        row.getCell(33).value = {
          formula: `AC${r}*X${r}`,
        } as any;
      } else {
        // NDF_CCY2 (default NDF)
        // MtM(CCY2) = direction * Amount * (ImpliedFwd - ContractedRate) * DF_ccy2
        row.getCell(33).value = {
          formula: `${dirFactor}*G${r}*(Y${r}-I${r})*AE${r}`,
        } as any;
        // MtM(CCY1) = MtM(CCY2) / Spot
        row.getCell(29).value = {
          formula: `AG${r}/X${r}`,
        } as any;
      }

      // S: MtM (overall) = MtM(CCY2) — the valuation currency
      row.getCell(19).value = {
        formula: `AG${r}`,
      } as any;

      // V: MtM Change = MtM - Prev MtM
      if (pv?.mtmAmount != null || pv?.mtmCcy2 != null) {
        row.getCell(22).value = {
          formula: `S${r}-U${r}`,
        } as any;
      }
    } else if (cv) {
      // Manual valuation — no formulas, just stored values
      row.getCell(25).value = cv.impliedForwardRate ?? null;    // Y
      row.getCell(26).value = cv.rCcy1 ?? null;                 // Z
      row.getCell(27).value = cv.dfCcy1 ?? null;                // AA
      row.getCell(28).value = cv.dccCcy1 || '';                 // AB
      row.getCell(29).value = cv.mtmCcy1 ?? null;               // AC
      row.getCell(30).value = cv.rCcy2 ?? null;                 // AD
      row.getCell(31).value = cv.dfCcy2 ?? null;                // AE
      row.getCell(32).value = cv.dccCcy2 || '';                 // AF
      row.getCell(33).value = cv.mtmCcy2 ?? cv.mtmAmount ?? null; // AG
      row.getCell(19).value = cv.mtmCcy2 ?? cv.mtmAmount ?? null; // S
      const prevMtm = pv?.mtmCcy2 ?? pv?.mtmAmount ?? null;
      const curMtm = cv.mtmCcy2 ?? cv.mtmAmount ?? null;
      row.getCell(22).value = curMtm != null && prevMtm != null ? curMtm - prevMtm : null; // V
    }

    // --- Forward subtype columns (AH=34 onwards) ---
    const isAsiatico = trade.isAverageRateForward;
    const isStarting = trade.isForwardStarting;
    // AH: Product Subtype
    row.getCell(34).value = isAsiatico ? 'Forward Asiático' : isStarting ? 'Forward Starting' : '';
    if (isAsiatico && cv) {
      // AI: Blended Rate
      row.getCell(35).value = cv.blendedRate ?? null;
      // AJ: Realised count
      row.getCell(36).value = cv.realisedCount ?? null;
      // AK: Projected count
      row.getCell(37).value = cv.projectedCount ?? null;
      // Per-date triplets (AL=38 onwards)
      const perDateRates = cv.perDateRates || [];
      perDateRates.forEach((entry: any, dateIdx: number) => {
        const baseCol = 38 + dateIdx * 3;
        row.getCell(baseCol).value = entry.date || '';     // Fixing Date N
        row.getCell(baseCol + 1).value = entry.rate ?? null; // Rate N
        row.getCell(baseCol + 2).value = entry.source || ''; // Source N
      });
    }

    // --- KAN-118: Methodology + IFRS 9 columns ---
    if (cv) {
      const b = kan118BaseCol;
      row.getCell(b).value = cv.forwardRateSource ?? null;        // Forward Rate Source
      row.getCell(b + 1).value = cv.discountingMethod ?? null;    // Discounting Method
      row.getCell(b + 2).value = cv.interpolatedFwdPoints ?? null; // Fwd Points Used
      row.getCell(b + 3).value = cv.undiscountedPnl ?? null;      // Undiscounted P&L

      // Spot at Trade
      row.getCell(b + 4).value = cv.spotAtTrade ?? null;
      // Fwd Points at Trade
      row.getCell(b + 5).value = cv.forwardPointsAtTrade ?? null;

      // Spot Element (formula when all inputs present, else stored value)
      // Formula: =IF(D{r}="Buy",1,-1)*G{r}*(X{r}-{spotAtTradeCol}{r})
      const spotAtTradeColLetter = colIndexToLetter(b + 4);
      if (isCalc && cv.spotAtTrade != null && cv.spotRate != null) {
        row.getCell(b + 6).value = {
          formula: `IF(D${r}="Buy",1,-1)*G${r}*(X${r}-${spotAtTradeColLetter}${r})`,
        } as any;
      } else {
        row.getCell(b + 6).value = cv.spotElement ?? null;
      }

      // Forward Element (stored value)
      row.getCell(b + 7).value = cv.forwardElement ?? null;

      // Annualisation methods (used by DF formula conditionals)
      row.getCell(b + 8).value = cv.annCcy1 || 'simple';
      row.getCell(b + 9).value = cv.annCcy2 || 'simple';
    }

  });

  // --- Column widths & number formats ---
  const numFmt4 = '#,##0.0000';
  const numFmt8 = '0.00000000';
  const numFmt0 = '#,##0';
  const numFmt2 = '#,##0.00';

  const colConfigs: Array<{ width: number; numFmt?: string }> = [
    /* A  */ { width: 10 },                     // Method
    /* B  */ { width: 12 },                     // Trade #
    /* C  */ { width: 14 },                     // Entity
    /* D  */ { width: 6 },                      // Direction
    /* E  */ { width: 10 },                     // Pair
    /* F  */ { width: 14 },                     // Settlement Type
    /* G  */ { width: 15, numFmt: numFmt0 },    // Amount (CCY1)
    /* H  */ { width: 15, numFmt: numFmt0 },    // Contra Amount (CCY2)
    /* I  */ { width: 12, numFmt: numFmt4 },    // Price
    /* J  */ { width: 20 },                     // Counterparty
    /* K  */ { width: 12 },                     // Trade Date
    /* L  */ { width: 12 },                     // Value Date
    /* M  */ { width: 12 },                     // Maturity Date
    /* N  */ { width: 14 },                     // Grouping 1
    /* O  */ { width: 14 },                     // Grouping 2
    /* P  */ { width: 14 },                     // Grouping 3
    /* Q  */ { width: 18 },                     // Hedge Object
    /* R  */ { width: 12 },                     // Valuation Date
    /* S  */ { width: 16, numFmt: numFmt2 },    // MtM (overall)
    /* T  */ { width: 12 },                     // Prev Val Date
    /* U  */ { width: 16, numFmt: numFmt2 },    // Prev MtM
    /* V  */ { width: 16, numFmt: numFmt2 },    // MtM Change
    /* W  */ { width: 6 },                      // Days
    /* X  */ { width: 12, numFmt: numFmt4 },    // Spot Rate
    /* Y  */ { width: 14, numFmt: '#,##0.00000000' }, // Implied Forward
    /* Z  */ { width: 14, numFmt: '0.000000000000' }, // r(CCY1)
    /* AA */ { width: 16, numFmt: '0.000000000000' }, // DF(CCY1)
    /* AB */ { width: 14 },                     // Day Count (CCY1)
    /* AC */ { width: 16, numFmt: numFmt2 },    // MtM(CCY1)
    /* AD */ { width: 14, numFmt: '0.000000000000' }, // r(CCY2)
    /* AE */ { width: 16, numFmt: '0.000000000000' }, // DF(CCY2)
    /* AF */ { width: 14 },                     // Day Count (CCY2)
    /* AG */ { width: 16, numFmt: numFmt2 },    // MtM(CCY2)
    // --- Forward Asiático columns ---
    /* AH */ { width: 16 },                     // Product Subtype
    /* AI */ { width: 14, numFmt: numFmt4 },    // Blended Rate
    /* AJ */ { width: 10 },                     // Realised #
    /* AK */ { width: 10 },                     // Projected #
  ];

  // Per-date triplet column configs
  for (let n = 0; n < maxFixingDates; n++) {
    colConfigs.push({ width: 12 });                    // Fixing Date N
    colConfigs.push({ width: 12, numFmt: numFmt4 });   // Rate N
    colConfigs.push({ width: 10 });                    // Source N
  }

  // KAN-118 column configs
  colConfigs.push({ width: 12 });                      // Forward Rate Source
  colConfigs.push({ width: 12 });                      // Discounting Method
  colConfigs.push({ width: 14, numFmt: numFmt8 });     // Fwd Points Used
  colConfigs.push({ width: 16, numFmt: numFmt2 });     // Undiscounted P&L
  colConfigs.push({ width: 12, numFmt: numFmt4 });     // Spot at Trade
  colConfigs.push({ width: 14, numFmt: numFmt4 });     // Fwd Points at Trade
  colConfigs.push({ width: 16, numFmt: numFmt2 });     // Spot Element
  colConfigs.push({ width: 16, numFmt: numFmt2 });     // Forward Element
  colConfigs.push({ width: 10 });                      // Ann (CCY1)
  colConfigs.push({ width: 10 });                      // Ann (CCY2)

  colConfigs.forEach((cfg, i) => {
    const col = worksheet.getColumn(i + 1);
    col.width = cfg.width;
    if (cfg.numFmt) {
      for (let r = 4; r <= trades.length + 3; r++) {
        const cell = worksheet.getCell(r, i + 1);
        if (cell.value != null) {
          cell.numFmt = cfg.numFmt;
        }
      }
    }
  });

  // Freeze header row
  worksheet.views = [{ state: 'frozen', ySplit: 3, xSplit: 0 }];

  // Download
  const safeDateStr = valuationDate.replace(/\//g, '-');
  const fileName = `MtM_Valuation_${safeDateStr}.xlsx`;
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  window.URL.revokeObjectURL(url);
};
